import React, { useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import type { DetailedProxyMetadata, SubjectRangeAnalysis } from 'venn-api';
import { Button, GetColor, Icon, getHelpCenterLinks } from 'venn-ui-kit';
import { getProxyTypeNoun, invalidate, plural, useHasFF, useModal } from 'venn-utils';
import { UserContext } from '../contexts';
import { type LayeredDetailedProxyMetadata, getFirstProxy } from '../hooks/useProxyDataQuery';
import { useInvestmentsReturnsRanges } from '../modals/pickers/proxy-picker/useInvestmentsReturnsRanges';
import { useProxyTypeValidation } from '../modals/pickers/proxy-picker/useProxyTypeValidation';
import type { FundToBulkProxy } from '../modals/pickers/types';
import { DeleteProxiesConfirmationModal } from './DeleteProxiesConfirmationModal';
import { useFirstLayerProxyTypeDropdownItems } from '../modals/pickers/proxy-picker/hooks/useProxyTypeDropdownItems';

interface ProxyMessageProps {
  proxyTypeNouns: string[];
  canManageProxy: boolean;
  maxFundsExceeded: boolean;
}
interface BulkProxySectionProps {
  selectedFunds: FundToBulkProxy[];
  proxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  holdingsProxyDataByFund: Record<string, DetailedProxyMetadata | undefined>;
  rangeDataByFund: Record<string, SubjectRangeAnalysis | undefined>;
  onClearSelected: () => void;

  onReturnsProxyPickerOpen: () => void;
  onHoldingsProxyPickerOpen: () => void;
}

const MAX_NUM_SELECTED_FUNDS = 50;

const ProxyMessage = ({ proxyTypeNouns, canManageProxy, maxFundsExceeded }: ProxyMessageProps) => {
  if (maxFundsExceeded) {
    return (
      <>
        <WarningTextWrapper>
          <Icon type="warning" /> You have exceeded the maximum number ({MAX_NUM_SELECTED_FUNDS}) of investments that
          can be selected
        </WarningTextWrapper>
        <SeparatorIcon type="pipe" />
        Deselect investments to Select a Proxy.
      </>
    );
  }

  if (!canManageProxy) {
    return (
      <>
        <WarningTextWrapper>
          <Icon type="warning" /> Setting proxies is not allowed
        </WarningTextWrapper>
        <SeparatorIcon type="pipe" />
        Contact your workspace administrator for more details.
      </>
    );
  }

  if (proxyTypeNouns.length === 0) {
    return (
      <>
        <WarningTextWrapper>
          <Icon type="warning" /> No proxy types available
        </WarningTextWrapper>
        <span className="text-nowrap">
          <SeparatorIcon type="pipe" className="mr-[6px]" />
          <Link href={getHelpCenterLinks().PROXY_FAQ_HREF} target="_blank" rel="noopener noreferrer">
            Learn&nbsp;more
          </Link>
        </span>
      </>
    );
  }

  return (
    <>
      <span className="mr-[6px]" data-testid="qa-available-proxy-types">
        {plural(proxyTypeNouns.length, {
          1: 'Available returns proxy type: ',
          other: 'Available returns proxy types: ',
        })}
        <Selected>{proxyTypeNouns.join(', ')}</Selected>
      </span>
      <span className="text-nowrap">
        <SeparatorIcon type="pipe" className="mr-[6px]" />
        <Link href={getHelpCenterLinks().PROXY_FAQ_HREF} target="_blank" rel="noopener noreferrer">
          Learn&nbsp;more
        </Link>
      </span>
    </>
  );
};

export const BulkProxySection = ({
  selectedFunds,
  proxyDataByFund,
  holdingsProxyDataByFund,
  onClearSelected,
  onReturnsProxyPickerOpen,
  onHoldingsProxyPickerOpen,
  rangeDataByFund,
}: BulkProxySectionProps) => {
  const holdingProxyFF = useHasFF('holding_proxy_ff');
  const hasLayeredProxyFF = useHasFF('layered_proxy_picker_ff');
  const hasExtrapolationFf = useHasFF('extrapolation_ff');

  const [isDeleteConfirmationOpen, openDeleteConfirmation, closeDeleteConfirmation] = useModal();
  const { hasPermission } = useContext(UserContext);

  const maxFundsExceeded = selectedFunds.length > MAX_NUM_SELECTED_FUNDS;

  const rawInvestmentReturnsRanges = useInvestmentsReturnsRanges(selectedFunds, proxyDataByFund, rangeDataByFund);
  const proxyTypeValidationResults = useProxyTypeValidation(
    selectedFunds,
    rawInvestmentReturnsRanges,
    undefined,
    undefined,
  );

  const selectableProxyTypes = Object.keys(proxyTypeValidationResults).filter(
    // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
    (x) => !proxyTypeValidationResults[x].hasOnlyErrors,
  ) as (keyof typeof proxyTypeValidationResults)[];

  const layeredProxyTypeDropdownItems = useFirstLayerProxyTypeDropdownItems({
    investments: selectedFunds,
    rawInvestmentRanges: rawInvestmentReturnsRanges,
    withExtrapolation: hasExtrapolationFf,
  });
  const availableLayeredProxyTypeLabels = layeredProxyTypeDropdownItems.map((item) => item.label);

  const proxyTypeNouns = hasLayeredProxyFF
    ? availableLayeredProxyTypeLabels
    : selectableProxyTypes.flatMap((proxyType) => getProxyTypeNoun(proxyType));

  const selectedFundsWithProxy = useMemo(
    () => selectedFunds.filter((investment) => getFirstProxy(proxyDataByFund[investment.id] ?? ([] as const))?.proxyId),
    [proxyDataByFund, selectedFunds],
  );

  const selectedFundsWithHoldingsProxy = useMemo(
    () => selectedFunds.filter((investment) => holdingsProxyDataByFund[investment.id]?.proxyId),
    [holdingsProxyDataByFund, selectedFunds],
  );

  const selectedFundIds = useMemo(() => {
    return new Set([...selectedFundsWithProxy.map((f) => f.id), ...selectedFundsWithHoldingsProxy.map((f) => f.id)]);
  }, [selectedFundsWithProxy, selectedFundsWithHoldingsProxy]);

  const selectedFundsWithAnyProxy = useMemo(() => {
    return selectedFunds.filter((fund) => selectedFundIds.has(fund.id));
  }, [selectedFunds, selectedFundIds]);

  const uniqueProxyIds = useMemo(() => {
    const holdingsProxyIds = Object.entries(holdingsProxyDataByFund)
      .filter(([fundId, _]) => selectedFundIds.has(fundId))
      .flatMap(([_, proxies]) => proxies ?? [])
      .map((proxy) => proxy.proxyId);
    const returnsProxyIds = Object.entries(proxyDataByFund)
      .filter(([fundId, _]) => selectedFundIds.has(fundId))
      .flatMap(([_, proxies]) => proxies ?? [])
      .map((proxy) => proxy.proxyId);
    return new Set([...holdingsProxyIds, ...returnsProxyIds]);
  }, [holdingsProxyDataByFund, proxyDataByFund, selectedFundIds]);

  const totalProxyCount = uniqueProxyIds.size;

  const proxySelectDisabled =
    selectedFunds.length === 0 ||
    selectableProxyTypes.length === 0 ||
    !hasPermission('MANAGE_PROXY') ||
    maxFundsExceeded;

  return (
    <>
      <StickyFooter showErrorBorder={maxFundsExceeded}>
        <DescriptionContainer>
          <TopDescription>
            {plural(selectedFunds.length, {
              1: '{{count}} investment selected',
              other: '{{count}} investments selected',
            })}
          </TopDescription>
          <ProxyMessage
            proxyTypeNouns={proxyTypeNouns}
            canManageProxy={hasPermission('MANAGE_PROXY')}
            maxFundsExceeded={maxFundsExceeded}
          />
        </DescriptionContainer>
        <RightContainer>
          {selectedFundsWithAnyProxy.length > 0 && (
            <Button
              onClick={openDeleteConfirmation}
              refreshedStyling
              destructive
              disabled={!hasPermission('MANAGE_PROXY')}
            >
              {totalProxyCount > 1 ? 'Remove proxies' : 'Remove proxy'}
            </Button>
          )}

          <Button onClick={onClearSelected} refreshedStyling>
            Clear Selection
          </Button>
          <Button dominant onClick={onReturnsProxyPickerOpen} refreshedStyling disabled={proxySelectDisabled}>
            Select a returns Proxy
          </Button>
          {holdingProxyFF && (
            <Button dominant onClick={onHoldingsProxyPickerOpen} refreshedStyling disabled={proxySelectDisabled}>
              Select a Holdings Proxy
            </Button>
          )}
        </RightContainer>
      </StickyFooter>

      {isDeleteConfirmationOpen && (
        <DeleteProxiesConfirmationModal
          fundsWithProxy={selectedFundsWithAnyProxy}
          proxyDataByFund={Object.fromEntries(Object.entries(proxyDataByFund).map(([key, value]) => [key, [...value]]))}
          holdingsProxyDataByFund={holdingsProxyDataByFund}
          onCancel={closeDeleteConfirmation}
          onFinishedUpdate={(updatedFundIds, _, { hasReturnsProxies }) => {
            closeDeleteConfirmation();
            invalidate(hasReturnsProxies ? 'investments' : 'investmentsHoldingsProxy', updatedFundIds);
          }}
        />
      )}
    </>
  );
};

const StickyFooter = styled.div<{ showErrorBorder: boolean }>`
  position: sticky;
  bottom: 0;
  background-color: ${GetColor.White};
  box-shadow: 0px 0px 8px 0px rgba(0, 0, 0, 0.25);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${({ showErrorBorder }) => css`
    border: 3px solid ${showErrorBorder ? GetColor.Error : 'transparent'};
  `}
`;

const DescriptionContainer = styled.div`
  position: relative;
  flex-grow: 1;

  .bulk-management-table-wrapper,
  & > div {
    width: 100%;
  }
`;

const TopDescription = styled.h4`
  font-weight: 700;
  margin: 0 0 2px;
`;

const SeparatorIcon = styled(Icon)`
  color: ${GetColor.GreyScale.Grey50};
`;

const Selected = styled.span`
  text-transform: capitalize;
`;

const Link = styled.a`
  font-weight: 700;
`;

const RightContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: flex-end;
`;

const WarningTextWrapper = styled.span`
  color: ${GetColor.Error};
`;
